import React, { useEffect, useState } from 'react'
import { FbButton, GridContainer, GridItem, Slider, Typography } from '@fabricks/ui-components'
import { Skeleton } from '@material-ui/lab'
import { useKeycloak } from 'react-keycloak'
import { get as _get, keyBy as _keyBy } from 'lodash'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import Swal from 'sweetalert2'
import axios from 'axios'
import { FormikHelpers } from 'formik/dist/types'
import { FormikValues } from 'formik'
import {
    BestBoxTypesAggDto,
    BoxTypeAggregateDto, ProcessLogMetaParamDto,
    useCalculateBestBoxTypesLazyQuery,
    useCurrentAggregateBoxTypesLazyQuery,
    useGetMeQuery,
    useGetProcessLogsLazyQuery,
    useOptimizeBoxFuelLevelsMutation,
} from '../../../api/models'
import { createBaseContext } from '../../../services/apiService'
import getEnvVars from '../../../environment/environment'
import { downloadFile, extractError } from '../../../utils/apiUtils'
import { ProcessOverview } from '../../../components/ProcessOverview/processOverview'
import { fuelLevelSettingFormConfig } from '../../Settings/forms/fuelLevelSettingFormConfig'
import { FormFeedback, FormGenerator } from '../../../utils/formGenerator'
import { bestBoxTypeOptFormConfig } from './boxTypeOptimizationFormConfig'
import { showInfo } from '../../../utils/sweetAlertHelper'

interface BoxTypeOptimizationProps {
    initLoadingTime?: number
}

export const BoxTypeOptimizationLoading: React.FC = () => {
    return (
        <>
            <Skeleton variant="rect" animation="wave" width="100%" height={200} />
            <Skeleton variant="text" animation="wave" width="100%" height={50} />
            <Skeleton variant="text" animation="wave" width="100%" height={50} />
        </>
    )
}

export const BoxTypeOptimization: React.FC<BoxTypeOptimizationProps> = (props: BoxTypeOptimizationProps) => {
    const { initLoadingTime } = props
    const [keycloak] = useKeycloak()
    const [initialValues, setInitialValues] = useState({
        desiredFuelLevel: 80,
        availableBoxes: [],
    })
    const [initLoading, setInitLoading] = useState((initLoadingTime || 0) > 0)
    const [barData, setBarData] = useState<any[]>([])
    const [currentBoxTypeReq, currentBoxTypeResp] = useCurrentAggregateBoxTypesLazyQuery()
    const [calculateBestBoxTypeReq] = useOptimizeBoxFuelLevelsMutation()
    const { data: meData, loading: meLoading, error: meError, called: meCalled } = useGetMeQuery(
        createBaseContext(keycloak.token || '')
    )

    useEffect(() => {
        if (meError) {
            alert('Your desired filling level could not be determined')
        }

        if (meCalled && !meLoading) {
            const targetValue = _get(meData, 'getMe.data.0.tenant.config.desiredFuelLevel', null)
            if (targetValue) {
                setInitialValues(Object.assign(initialValues, { desiredFuelLevel: targetValue * 100 }))
            }
        }
        return () => {}
    }, [meData, meError, meCalled, meLoading, initialValues])

    useEffect(() => {
        if (keycloak.token && !currentBoxTypeResp.called) {
            currentBoxTypeReq(createBaseContext(keycloak.token))
        }
        if (currentBoxTypeResp.called && currentBoxTypeResp.data) {
            const currentBoxTypeData = _get(currentBoxTypeResp, 'data.getStatistics.aggregateBoxTypes', []).map(
                (entry: BoxTypeAggregateDto) => {
                    const name = _get(entry, 'boxType.name', '-')
                    const uuid = _get(entry, 'boxType.uuid', '-')
                    // @ts-ignore
                    const newVal = 0
                    return {
                        name,
                        uuid,
                        all: _get(entry, 'count', 0),
                    }
                }
            )
            setBarData(currentBoxTypeData)
        }
        return () => {}
    }, [currentBoxTypeReq, currentBoxTypeResp, keycloak.token])




    const onSubmit = (
        values: any,
        actions: FormikHelpers<FormikValues>,
        setFeedback: React.Dispatch<React.SetStateAction<FormFeedback>>
    ): void => {
        const params = createBaseContext(keycloak.token || '', {
            fuelLevel: values.desiredFuelLevel / 100,
            availableBoxes: values.availableBoxes,
        })

        const submitForm = (): void => {
            calculateBestBoxTypeReq(params)
                .then((response: any) => {
                    const error = extractError(response, 'optimizeBoxFuelLevels')
                    if (error) {
                        return setFeedback({
                            type: 'error',
                            message: `${_get(error, 'message', 'unknown')} (${_get(error, 'code', 'XXX')})`,
                        })
                    }
                    return setFeedback({
                        type: 'success',
                        message: 'Successfully planned the optimization execution',
                        timeout: 2500,
                    })
                })
                .catch(err => {
                    setFeedback({ type: 'error', message: err })
                })
                .finally(() => {
                    actions.setSubmitting(false)
                })
        }
        setTimeout(submitForm, 1500)
    }

    const handleDownload = (metaUuid: string) => {
        Swal.fire({
            title: `Download`,
            text: 'The optimization result is ready and can be downloaded',
            showLoaderOnConfirm: true,
            icon: 'success',
            confirmButtonText: 'Download',
            preConfirm: () => {
                return axios
                    .get(`${getEnvVars.bff.host}/optimization/fuel-level/${metaUuid}`, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            Authorization: keycloak.token ? `Bearer ${keycloak.token}` : '',
                        },
                        responseType: 'blob',
                    })
                    .then(downloadFile)
            },
        })
    }

    if (initLoading) {
        setTimeout(() => setInitLoading(false), initLoadingTime)
        return <BoxTypeOptimizationLoading />
    }

    const customAxisTick = (props: any) => {
        const { x, y, stroke, payload } = props
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-35)">
                    {payload.value}
                </text>
            </g>
        )
    }

    const renderChart = () => {
        return (
            <ResponsiveContainer width="100%" height={355} className="box-type-opt-chart-container">
                <BarChart
                    className="box-type-opt-current-chart"
                    height={350}
                    data={barData}
                    margin={{
                        top: 10,
                        right: 15,
                        left: -10,
                        bottom: 70,
                    }}
                >
                    <CartesianGrid strokeDasharray="4 4" />
                    <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        tick={customAxisTick}
                        tickCount={barData.length}
                        tickSize={barData.length}
                    />
                    <YAxis stroke="#333" />
                    <Tooltip labelStyle={{ color: '#000' }} />
                    <Legend layout="horizontal" verticalAlign="top" align="center" margin={{ bottom: 70 }} height={40} wrapperStyle={{marginBottom: 15}} />
                    <Bar dataKey="all" fill="#00FFFF" />
                    <Bar dataKey="current" fill="rgba(254, 85, 112, 1.0)" />
                    <Bar dataKey="new" fill="#daf967" />
                    <Bar dataKey="best" fill="#32ff7e" />
                </BarChart>
            </ResponsiveContainer>
        )
    }

    const onRowClick = (uuid: string, data: any) => {
        const isLoading = _get(data, 'status', null) === 'IN_PROGRESS'
        const meta = _get(data, 'meta', null)
        if (isLoading) {
            return showInfo('The process is not done yet. We will try to accelerate the process', 'Not ready')
        }
        if (meta) {
            return handleDownload(meta)
        }
    }

    const onShowResult = (result: ProcessLogMetaParamDto[]) => {
        const newAmount = JSON.parse(_get(result, '0.value', {}))
        const currentAmount = JSON.parse(_get(result, '1.value', {}))
        const bestAmount = JSON.parse(_get(result, '2.value', {}))
        const currentBoxTypeData = _keyBy(
            _get(currentBoxTypeResp, 'data.getStatistics.aggregateBoxTypes', []).map((exEntry: any) => ({
                uuid: _get(exEntry, 'boxType.uuid', '-'),
                count: _get(exEntry, 'count', 0),
            })),
            'uuid'
        )
        const newBarData: any[] = barData.map((entry: any) => {
            // eslint-disable-next-line no-param-reassign
            entry.all = _get(currentBoxTypeData, `${entry.uuid}.count`, 0)
            // eslint-disable-next-line no-param-reassign
            entry.current = _get(currentAmount, entry.uuid, 0)
            // eslint-disable-next-line no-param-reassign
            entry.new = _get(newAmount, entry.uuid, 0)
            // eslint-disable-next-line no-param-reassign
            entry.best = _get(bestAmount, entry.uuid, 0)
            return entry
        })
        setBarData(newBarData)
    }

    return (
        <div style={{ marginBottom: '15px' }}>
            <div>{renderChart()}</div>
            <br />
            <br />
            <GridContainer>
                <GridItem xs={12} sm={6} md={6}>
                    <div style={{ position: 'relative' }}>
                        <FormGenerator
                            id="best-box-type-opt"
                            config={bestBoxTypeOptFormConfig}
                            submitLabel="Execute"
                            initialValues={initialValues}
                            onSubmit={onSubmit}
                        />
                    </div>
                    <Typography variant="caption">
                        The amount of available boxes will be calculated based on your imported warehouse data.
                    </Typography>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                    <ProcessOverview type={'OPTIMIZE_FUEL_LEVEL'} onShowResult={onShowResult} />
                </GridItem>
            </GridContainer>
        </div>
    )
}
